/**
 * One connector, drawn as SVG.
 *
 * The polyline arrives already orthogonal and already normalised from the
 * router; nothing here may change its geometry. The only visual liberty taken
 * is a small corner radius, applied by `polylineToPath`, which rounds the
 * corners without moving them.
 */
import { memo } from 'react';
import type { GraphEdge } from '../graph/model/types';
import { polylineToPath } from '../graph/routing/polyline';
import { arrowHead, terminalDirection, trimForArrow } from './shapes';
import { edgeColor } from './palette';

export interface DiagramEdgeProps {
  readonly edge: GraphEdge;
  readonly highlighted: boolean;
  readonly muted: boolean;
  readonly selected: boolean;
  readonly cornerRadius: number;
  readonly fontSize: number;
  readonly onSelect: (id: string) => void;
  readonly onHover: (id: string | null) => void;
}

const ARROW_SIZE = 8;

function DiagramEdgeImpl({
  edge,
  highlighted,
  muted,
  selected,
  cornerRadius,
  fontSize,
  onSelect,
  onHover,
}: DiagramEdgeProps) {
  if (edge.points.length < 2) return null;

  const trimmed = trimForArrow(edge.points, ARROW_SIZE - 1);
  const path = polylineToPath(trimmed, cornerRadius);
  const direction = terminalDirection(edge.points);
  const head = arrowHead(edge.points[edge.points.length - 1]!, direction, ARROW_SIZE);

  const colour = selected || highlighted ? 'var(--fs-selection)' : edgeColor(edge.isBackEdge);
  const width = selected ? 2.4 : highlighted ? 2 : 1.5;

  // Back edges are dashed as well as coloured: branch identity must not depend
  // on colour alone.
  const dash = edge.isBackEdge ? '7 5' : undefined;

  return (
    <g
      opacity={muted ? 0.2 : 1}
      data-edge-id={edge.id}
      onPointerEnter={() => onHover(edge.id)}
      onPointerLeave={() => onHover(null)}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(edge.id);
      }}
    >
      {/* Invisible fat stroke so the edge is easy to hit with a finger. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        style={{ cursor: 'pointer' }}
      />
      <path
        d={path}
        fill="none"
        stroke={colour}
        strokeWidth={width}
        strokeDasharray={dash}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ pointerEvents: 'none' }}
      />
      <path d={head} fill={colour} style={{ pointerEvents: 'none' }} />

      {edge.label && edge.labelPosition ? (
        <EdgeLabel
          text={edge.label}
          x={edge.labelPosition.x}
          y={edge.labelPosition.y}
          fontSize={fontSize}
          highlighted={selected || highlighted}
        />
      ) : null}
    </g>
  );
}

interface EdgeLabelProps {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly fontSize: number;
  readonly highlighted: boolean;
}

function EdgeLabel({ text, x, y, fontSize, highlighted }: EdgeLabelProps) {
  // Estimating the plate width from the character count is enough here — the
  // plate only has to stop the line showing through the glyphs.
  const width = text.length * fontSize * 0.58 + 10;
  const height = fontSize + 6;

  return (
    <g style={{ pointerEvents: 'none' }}>
      <rect
        x={x - width / 2}
        y={y - height / 2}
        width={width}
        height={height}
        rx={4}
        fill="var(--fs-edge-label-bg)"
        opacity={0.92}
      />
      <text
        x={x}
        y={y + fontSize * 0.35}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight={highlighted ? 650 : 550}
        fill={highlighted ? 'var(--fs-selection)' : 'var(--fs-edge-label)'}
        style={{ userSelect: 'none' }}
      >
        {text}
      </text>
    </g>
  );
}

export const DiagramEdge = memo(DiagramEdgeImpl);
